import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { getOpeningBalances, saveOpeningBalances } from '../../api/accounting'
import { getControlAccounts, getSetupStandard } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import { money } from '../sales/salesShared'
import { ACCOUNT_TYPES } from '../accounting/accountingShared'
import { MasterTabs, useOnboardingStep } from './masterShared'
import OnboardingContinuePrompt from '../../components/onboarding/OnboardingContinuePrompt'
import OnboardingStepBar from '../../components/onboarding/OnboardingStepBar'
import { OpeningBalancesGuide } from './MasterGuides'

const amt = (v) => Math.round((Number(v) || 0) * 100) / 100
const show = (n) => (n ? String(n) : '')

// Opening balances: what each account held on the day the company started
// using ASYS, typed in one column per side. Each saves as an entry against
// Opening balance equity; the page checks that debits equal credits.
export default function OpeningBalances() {
  const qc = useQueryClient()
  const { notify } = useToast()
  const onboarding = useOnboardingStep('opening')
  const { data, isLoading } = useQuery({ queryKey: ['opening-balances'], queryFn: () => getOpeningBalances() })
  const { data: controls = [] } = useQuery({ queryKey: ['control-accounts'], queryFn: getControlAccounts })
  const { data: standard } = useQuery({ queryKey: ['setup-standard'], queryFn: getSetupStandard })
  const [date, setDate] = useState('')
  const [edits, setEdits] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (data?.date) setDate((d) => d || data.date) }, [data])

  // Receivables, payables and stock: a lump sum isn't split by customer, vendor or item.
  const lumpNote = useMemo(() => {
    const code = (k) => String(controls.find((c) => c.name === k)?.account_code || '').trim() || (standard?.defaults || []).find((d) => d.name === k)?.code
    return { [code('AR')]: 'Customers’ balances and aging won’t include this amount.', [code('AP')]: 'Vendors’ balances won’t include this amount.', [code('Inventory')]: 'Stock value by item won’t include this amount.' }
  }, [controls, standard])

  const accounts = data?.accounts || []
  const value = (a, side) => edits[a.account_id]?.[side] ?? show(a.opening?.[side])
  const setValue = (a, side, v) => setEdits((e) => {
    const cur = e[a.account_id] || { dr: show(a.opening?.dr), cr: show(a.opening?.cr) }
    // A balance is a debit or a credit: typing one clears the other.
    return { ...e, [a.account_id]: { ...cur, [side]: v, ...(v !== '' ? { [side === 'dr' ? 'cr' : 'dr']: '' } : {}) } }
  })
  const entryRows = accounts.filter((a) => !a.heading && !a.is_equity_account)
  const totals = entryRows.reduce((t, a) => ({ dr: t.dr + amt(value(a, 'dr')), cr: t.cr + amt(value(a, 'cr')) }), { dr: 0, cr: 0 })
  const diff = amt(totals.dr - totals.cr)
  const changed = entryRows.filter((a) => {
    if (!edits[a.account_id]) return false
    const dr = amt(value(a, 'dr')); const cr = amt(value(a, 'cr'))
    return dr !== amt(a.opening?.dr) || cr !== amt(a.opening?.cr) || (a.opening && (dr || cr) && a.opening.date !== date)
  })
  const groups = ACCOUNT_TYPES.map(([type, label]) => [label, accounts.filter((a) => a.type === type)]).filter(([, list]) => list.length)

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const res = await saveOpeningBalances({ entry_date: date, entries: changed.map((a) => ({ account_id: a.account_id, dr: amt(value(a, 'dr')), cr: amt(value(a, 'cr')) })) })
      setEdits({})
      qc.setQueryData(['opening-balances'], res)
      await Promise.all(['accounts', 'control-accounts'].map((k) => qc.invalidateQueries({ queryKey: [k] })))
      notify(`${res.changed} opening balance${res.changed === 1 ? '' : 's'} saved`)
      onboarding.afterSave()
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Couldn’t save the opening balances.')
    } finally {
      setSaving(false)
    }
  }

  const input = (a, side) => (
    <input className="ci-form-cell-input num" inputMode="decimal" placeholder="0.00" disabled={a.locked} value={value(a, side)}
      onChange={(e) => setValue(a, side, e.target.value.replace(/[^\d.]/g, ''))} aria-label={`${a.code} ${side === 'dr' ? 'debit' : 'credit'}`} />
  )

  return (
    <div className="ci-page">
      <MasterTabs />
      <OnboardingStepBar step="opening" />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Opening balances <HelpTip text="What each account held on the day you started using ASYS, for moving your books in. Type each balance in its debit or credit column. The other side of each goes to Opening balance equity, which should come to zero when everything is in." /></h1>
            <div className="ci-title-meta">What each account held when you moved your books to ASYS.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-ghost" to="/master/accounts">Chart of accounts</Link>
          <button type="button" className="ci-btn ci-btn-primary" disabled={!changed.length || saving || !date} onClick={save}><Save size={15} />{saving ? 'Saving…' : `Save${changed.length ? ` ${changed.length}` : ''}`}</button>
        </div>
      </div>

      <OpeningBalancesGuide />
      {error && <ErrorBanner message={error} />}

      {isLoading ? <Spinner label="Loading opening balances…" /> : (
        <>
          <div className="ci-form-meta-card ob-head">
            <label className="ci-form-field">
              <span>As of <HelpTip text="The day your old books stopped and ASYS started, usually the last day of a month or year. Balances you change are saved as of this date." /></span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <div className={`ob-check ${diff ? 'is-off' : 'is-ok'}`}>
              {diff
                ? <><StatusBadge label={`Out by ${money(Math.abs(diff))}`} tone="warn" /> Debits are {diff > 0 ? 'more' : 'less'} than credits. The difference sits in Opening balance equity until it’s corrected; check your old trial balance.</>
                : (totals.dr ? <><StatusBadge label="Balanced" tone="good" /> Debits and credits are equal.</> : 'Enter each account’s balance in its debit or credit column.')}
            </div>
          </div>

          {groups.map(([label, list]) => (
            <section key={label} className="ci-table-wrap mf-default-group">
              <h2 className="mf-default-title">{label}</h2>
              <div className="doc-table-scroll">
                <table className="ci-line-table ob-table">
                  <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
                  <tbody>
                    {list.map((a) => {
                      if (a.heading) return <tr key={a.account_id} className="ob-heading"><td colSpan="3">{a.code} · {a.name}</td></tr>
                      if (a.is_equity_account) {
                        return (
                          <tr key={a.account_id} className="ob-equity">
                            <td>{a.code} · {a.name}<span className="cell-sub">Takes the other side of every opening balance.</span></td>
                            <td className="num">{diff < 0 ? money(-diff) : ''}</td>
                            <td className="num">{diff > 0 ? money(diff) : ''}</td>
                          </tr>
                        )
                      }
                      const note = lumpNote[a.code]
                      return (
                        <tr key={a.account_id} className={changed.includes(a) ? 'changed' : ''}>
                          <td>
                            {a.code} · {a.name}
                            {a.locked && <span className="cell-sub">Its month is closed; change it with a journal entry.</span>}
                            {!a.locked && a.opening && a.opening.date !== date && <span className="cell-sub">As of {fmtDay(a.opening.date)}</span>}
                            {note && (amt(value(a, 'dr')) || amt(value(a, 'cr'))) ? <span className="cell-sub">{note}</span> : null}
                          </td>
                          <td className="ob-cell">{input(a, 'dr')}</td>
                          <td className="ob-cell">{input(a, 'cr')}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <div className="ci-form-meta-card ob-totals">
            <span>Total debits <strong>{money(totals.dr)}</strong></span>
            <span>Total credits <strong>{money(totals.cr)}</strong></span>
            <span className={diff ? 'ob-off' : ''}>Difference <strong>{money(Math.abs(diff))}</strong></span>
          </div>
        </>
      )}
      <OnboardingContinuePrompt open={onboarding.open} saved="Opening balances saved" another="Keep entering balances" next={onboarding.after} onAddAnother={onboarding.addAnother} onNext={onboarding.next} />
    </div>
  )
}
